/**
 * Top-level React error boundary.
 *
 * Catches render errors anywhere in the tree (including async errors
 * that escape into render, type errors from a malformed API response,
 * and any uncaught throw from a child component). Without this, a
 * single bad render takes the whole SPA to a white screen.
 *
 * Auth-redirect collisions: api.ts may set window.location.href on a
 * 401, but the redirect doesn't navigate synchronously — the in-flight
 * render can still throw before the page unloads. The boundary catches
 * that case too, but we don't reset on it (the redirect will land
 * shortly).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only — we don't have Sentry yet on the MVP slice. When
    // we wire one up, this is the obvious hook point.
    // eslint-disable-next-line no-console
    console.error('UI error boundary caught:', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  return (
    <div
      role="alert"
      className="max-w-md mx-auto px-4 py-20 text-center"
    >
      <h1 className="mp-display text-3xl text-tarot-gold mb-3">出了點問題</h1>
      <p className="text-tarot-muted text-sm mb-6 break-words">
        頁面遇到無法處理的錯誤。請試著回到首頁或重新整理。
      </p>
      {/* Show the technical message only on dev/preview hosts so users
          don't see a stack-trace-looking string in production. Same
          envelope as the dev panel: if the backend reports dev mode,
          this is fine to expose. */}
      {import.meta.env.DEV && (
        <pre className="text-left text-xs text-tarot-muted bg-tarot-card border border-tarot-purple/20 rounded p-3 mb-6 overflow-auto max-h-48">
          {error.message}
        </pre>
      )}
      <div className="flex justify-center gap-3">
        <button
          type="button"
          onClick={onReset}
          className="btn-ghost"
        >
          重試
        </button>
        <button
          type="button"
          onClick={() => {
            window.location.href = '/';
          }}
          className="btn-primary"
        >
          回首頁
        </button>
      </div>
    </div>
  );
}
